import React, { useEffect, useState, Component } from 'react';
import { format, isToday, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Check, Edit, Trash2, Plus, BookOpen, PenLine, Video, Clock, X, CalendarIcon, FileText } from 'lucide-react';
import { DERS_RENKLERI } from '../utils/constants';
import { ScheduleSummary } from '../components/ScheduleSummary';
import { useProgramContext } from '../contexts/ProgramContext';
interface ScheduleItem {
  id: string;
  activityType: string;
  subject: string;
  startTime: string;
  endTime: string;
  questionCount?: number;
  notes?: string;
  completed: boolean;
  isInstitutional?: boolean;
}
// Normalize subject name to ensure consistent color mapping
const normalizeSubjectName = (subject: string) => {
  const subjectUpper = subject.toUpperCase();
  // Handle different variations of subject names
  if (subjectUpper.includes('TÜRKÇE') || subjectUpper.includes('TURKCE')) return 'TÜRKÇE';
  if (subjectUpper.includes('MATEMATİK') || subjectUpper.includes('MATEMATİK')) return 'MATEMATİK';
  if (subjectUpper.includes('FEN')) return 'FEN BİLİMLERİ';
  if (subjectUpper.includes('İNKILAP') || subjectUpper.includes('INKILAP') || subjectUpper.includes('TARİH')) return 'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK';
  if (subjectUpper.includes('İNGİLİZCE') || subjectUpper.includes('INGILIZCE')) return 'İNGİLİZCE';
  if (subjectUpper.includes('DİN') || subjectUpper.includes('DIN')) return 'DİN KÜLTÜRÜ VE AHLÂK BİLGİSİ';
  return subject;
};
// Add Event Modal Component
function AddEventModal({
  isOpen,
  onClose,
  onAddEvent,
  selectedDay
}: {
  isOpen: boolean;
  onClose: () => void;
  onAddEvent: (event: Omit<ScheduleItem, 'id' | 'completed'>) => void;
  selectedDay: string;
}) {
  const [subject, setSubject] = useState('TÜRKÇE');
  const [activityType, setActivityType] = useState('study');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [questionCount, setQuestionCount] = useState<number | undefined>(undefined);
  const [notes, setNotes] = useState('');
  if (!isOpen) return null;
  // Available time slots for selection
  const timeSlots = Array.from({
    length: 24
  }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });
  // LGS müfredatına uygun dersler
  const subjects = ['TÜRKÇE', 'MATEMATİK', 'FEN BİLİMLERİ', 'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK', 'İNGİLİZCE', 'DİN KÜLTÜRÜ VE AHLÂK BİLGİSİ'];
  const activityTypes = [{
    id: 'study',
    label: 'Konu Çalışma'
  }, {
    id: 'practice',
    label: 'Soru Çözüm'
  }, {
    id: 'exam',
    label: 'Deneme'
  }];
  const handleSubmit = () => {
    onAddEvent({
      subject,
      activityType,
      startTime,
      endTime,
      questionCount: activityType !== 'study' ? questionCount : undefined,
      notes
    });
    onClose();
  };
  return <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50 overflow-y-auto" data-id="element-3062">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-xl" data-id="element-3063">
        <div className="relative bg-black/90 rounded-xl" data-id="element-3064">
          <div className="p-4 border-b border-gray-800 flex justify-between items-center" data-id="element-3065">
            <h2 className="text-xl font-bold" data-id="element-3066">{selectedDay} - Etkinlik Ekle</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white" data-id="element-3067">
              <X className="w-6 h-6" data-id="element-3068" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-3069">
            <div className="space-y-2" data-id="element-3070">
              <label className="text-sm font-medium text-gray-300" data-id="element-3071">
                Aktivite Tipi
              </label>
              <select value={activityType} onChange={e => setActivityType(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              backgroundSize: '1em'
            }} data-id="element-3072">
                {activityTypes.map(type => <option key={type.id} value={type.id} data-id="element-3073">
                    {type.label}
                  </option>)}
              </select>
              <style jsx global data-id="element-3074">{`
                select option {
                  background-color: #1a1a1a;
                  color: white;
                }
                select option:hover,
                select option:focus,
                select option:active,
                select option:checked {
                  background-color: #6d28d9 !important;
                  color: white !important;
                }
              `}</style>
            </div>
            <div className="space-y-2" data-id="element-3075">
              <label className="text-sm font-medium text-gray-300" data-id="element-3076">Ders</label>
              <select value={subject} onChange={e => setSubject(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              backgroundSize: '1em'
            }} data-id="element-3077">
                {subjects.map(subject => <option key={subject} value={subject} data-id="element-3078">
                    {subject}
                  </option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-3079">
              <div className="space-y-2" data-id="element-3080">
                <label className="text-sm font-medium text-gray-300" data-id="element-3081">
                  Başlangıç Saati
                </label>
                <select value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1em'
              }} data-id="element-3082">
                  {timeSlots.map(time => <option key={time} value={time} data-id="element-3083">
                      {time}
                    </option>)}
                </select>
              </div>
              <div className="space-y-2" data-id="element-3084">
                <label className="text-sm font-medium text-gray-300" data-id="element-3085">
                  Bitiş Saati
                </label>
                <select value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1em'
              }} data-id="element-3086">
                  {timeSlots.map(time => <option key={time} value={time} data-id="element-3087">
                      {time}
                    </option>)}
                </select>
              </div>
            </div>
            {activityType !== 'study' && <div className="space-y-2" data-id="element-3088">
                <label className="text-sm font-medium text-gray-300" data-id="element-3089">
                  Soru Sayısı
                </label>
                <input type="number" min="0" value={questionCount || ''} onChange={e => setQuestionCount(e.target.value ? parseInt(e.target.value) : undefined)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" data-id="element-3090" />
              </div>}
            <div className="space-y-2" data-id="element-3091">
              <label className="text-sm font-medium text-gray-300" data-id="element-3092">
                Notlar
              </label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3 min-h-[100px]" placeholder="Notlarınızı buraya ekleyin..." data-id="element-3093" />
            </div>
          </div>
          <div className="p-4 border-t border-gray-800 flex justify-end gap-3" data-id="element-3094">
            <button onClick={onClose} className="px-4 py-2 bg-transparent border border-gray-700 hover:bg-white/5 text-gray-300 rounded-lg transition-colors" data-id="element-3095">
              İptal
            </button>
            <button onClick={handleSubmit} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors" data-id="element-3096">
              Ekle
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Edit Modal Component
function EditModal({
  item,
  day,
  onClose,
  onSave
}: {
  item: ScheduleItem | null;
  day: string;
  onClose: () => void;
  onSave: (day: string, item: ScheduleItem) => void;
}) {
  const [editedItem, setEditedItem] = useState<ScheduleItem | null>(item);
  if (!editedItem) return null;
  const handleChange = (field: string, value: any) => {
    setEditedItem(prev => {
      if (!prev) return null;
      return {
        ...prev,
        [field]: value
      };
    });
  };
  const handleSave = () => {
    if (editedItem) {
      onSave(day, editedItem);
      onClose();
    }
  };
  // Available time slots for selection
  const timeSlots = Array.from({
    length: 24
  }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });
  // LGS müfredatına uygun dersler
  const subjects = ['TÜRKÇE', 'MATEMATİK', 'FEN BİLİMLERİ', 'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK', 'İNGİLİZCE', 'DİN KÜLTÜRÜ VE AHLÂK BİLGİSİ'];
  const activityTypes = [{
    id: 'study',
    label: 'Konu Çalışma'
  }, {
    id: 'practice',
    label: 'Soru Çözüm'
  }, {
    id: 'exam',
    label: 'Deneme'
  }];
  return <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50 overflow-y-auto" data-id="element-3097">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-xl" data-id="element-3098">
        <div className="relative bg-black/90 rounded-xl" data-id="element-3099">
          <div className="p-4 border-b border-gray-800 flex justify-between items-center" data-id="element-3100">
            <h2 className="text-xl font-bold" data-id="element-3101">Programı Düzenle</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white" data-id="element-3102">
              <X className="w-6 h-6" data-id="element-3103" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-3104">
            <div className="space-y-2" data-id="element-3105">
              <label className="text-sm font-medium text-gray-300" data-id="element-3106">
                Aktivite Tipi
              </label>
              <select value={editedItem.activityType} onChange={e => handleChange('activityType', e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              backgroundSize: '1em'
            }} data-id="element-3107">
                {activityTypes.map(type => <option key={type.id} value={type.id} data-id="element-3108">
                    {type.label}
                  </option>)}
              </select>
              <style jsx global data-id="element-3109">{`
                select option {
                  background-color: #1a1a1a;
                  color: white;
                }
                select option:hover,
                select option:focus,
                select option:active,
                select option:checked {
                  background-color: #6d28d9 !important;
                  color: white !important;
                }
              `}</style>
            </div>
            <div className="space-y-2" data-id="element-3110">
              <label className="text-sm font-medium text-gray-300" data-id="element-3111">Ders</label>
              <select value={editedItem.subject} onChange={e => handleChange('subject', e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              backgroundSize: '1em'
            }} data-id="element-3112">
                {subjects.map(subject => <option key={subject} value={subject} data-id="element-3113">
                    {subject}
                  </option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-3114">
              <div className="space-y-2" data-id="element-3115">
                <label className="text-sm font-medium text-gray-300" data-id="element-3116">
                  Başlangıç Saati
                </label>
                <select value={editedItem.startTime} onChange={e => handleChange('startTime', e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1em'
              }} data-id="element-3117">
                  {timeSlots.map(time => <option key={time} value={time} data-id="element-3118">
                      {time}
                    </option>)}
                </select>
              </div>
              <div className="space-y-2" data-id="element-3119">
                <label className="text-sm font-medium text-gray-300" data-id="element-3120">
                  Bitiş Saati
                </label>
                <select value={editedItem.endTime} onChange={e => handleChange('endTime', e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1em'
              }} data-id="element-3121">
                  {timeSlots.map(time => <option key={time} value={time} data-id="element-3122">
                      {time}
                    </option>)}
                </select>
              </div>
            </div>
            {editedItem.activityType !== 'study' && <div className="space-y-2" data-id="element-3123">
                <label className="text-sm font-medium text-gray-300" data-id="element-3124">
                  Soru Sayısı
                </label>
                <input type="number" min="0" value={editedItem.questionCount || ''} onChange={e => handleChange('questionCount', parseInt(e.target.value) || 0)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3" data-id="element-3125" />
              </div>}
            <div className="space-y-2" data-id="element-3126">
              <label className="text-sm font-medium text-gray-300" data-id="element-3127">
                Notlar
              </label>
              <textarea value={editedItem.notes || ''} onChange={e => handleChange('notes', e.target.value)} className="w-full bg-white/5 text-white rounded-lg px-4 py-3 min-h-[100px]" placeholder="Notlarınızı buraya ekleyin..." data-id="element-3128" />
            </div>
          </div>
          <div className="p-4 border-t border-gray-800 flex justify-end gap-3" data-id="element-3129">
            <button onClick={onClose} className="px-4 py-2 bg-transparent border border-gray-700 hover:bg-white/5 text-gray-300 rounded-lg transition-colors" data-id="element-3130">
              İptal
            </button>
            <button onClick={handleSave} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors" data-id="element-3131">
              Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Confirmation Dialog Component
function ConfirmDialog({
  isOpen,
  message,
  onConfirm,
  onCancel
}: {
  isOpen: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50" data-id="element-3132">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-md" data-id="element-3133">
        <div className="relative bg-black/90 rounded-xl" data-id="element-3134">
          <div className="p-6" data-id="element-3135">
            <h3 className="text-lg font-medium mb-4" data-id="element-3136">{message}</h3>
            <div className="flex justify-end gap-3" data-id="element-3137">
              <button onClick={onCancel} className="px-4 py-2 bg-transparent border border-gray-700 hover:bg-white/5 text-gray-300 rounded-lg transition-colors" data-id="element-3138">
                İptal
              </button>
              <button onClick={onConfirm} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors" data-id="element-3139">
                Evet, Kaldır
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// Day Schedule Component for displaying activities of a selected day
function DaySchedule({
  scheduleItems,
  selectedDay,
  onToggleCompletion,
  onEditItem,
  onDeleteItem
}: {
  scheduleItems: Record<string, ScheduleItem[]>;
  selectedDay: string;
  onToggleCompletion: (day: string, id: string) => void;
  onEditItem: (item: ScheduleItem) => void;
  onDeleteItem: (day: string, id: string) => void;
}) {
  const dayItems = scheduleItems[selectedDay] || [];
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'study':
        return BookOpen;
      case 'practice':
        return PenLine;
      case 'exam':
        return Video;
      case 'video':
        return Video;
      case 'resource':
        return BookOpen;
      case 'test':
        return PenLine;
      case 'homework':
        return FileText;
      default:
        return Clock;
    }
  };
  const normalizeSubjectName = (subject: string) => {
    const subjectUpper = subject.toUpperCase();
    // Handle different variations of subject names
    if (subjectUpper.includes('TÜRKÇE') || subjectUpper.includes('TURKCE')) return 'TÜRKÇE';
    if (subjectUpper.includes('MATEMATİK') || subjectUpper.includes('MATEMATİK')) return 'MATEMATİK';
    if (subjectUpper.includes('FEN')) return 'FEN BİLİMLERİ';
    if (subjectUpper.includes('İNKILAP') || subjectUpper.includes('INKILAP') || subjectUpper.includes('TARİH')) return 'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK';
    if (subjectUpper.includes('İNGİLİZCE') || subjectUpper.includes('INGILIZCE')) return 'İNGİLİZCE';
    if (subjectUpper.includes('DİN') || subjectUpper.includes('DIN')) return 'DİN KÜLTÜRÜ VE AHLÂK BİLGİSİ';
    return subject;
  };
  return <div className="space-y-4 mt-4" data-id="element-3140">
      {dayItems.length > 0 ? dayItems.sort((a, b) => a.startTime.localeCompare(b.startTime)).map(item => {
      const Icon = getActivityIcon(item.activityType);
      const normalizedSubject = normalizeSubjectName(item.subject);
      const gradientClass = DERS_RENKLERI[normalizedSubject] || 'from-indigo-600 to-purple-600';
      if (item.isInstitutional) {
        return <div key={item.id} className={`p-4 rounded-lg bg-gradient-to-r ${gradientClass} ${item.completed ? 'opacity-60' : ''}`} data-id="element-3141">
                  <div className="flex items-center justify-between mb-2" data-id="element-3142">
                    <h3 className="text-lg font-semibold" data-id="element-3143">{item.subject}</h3>
                    <span className="text-sm text-white/90" data-id="element-3144">
                      {item.startTime.substring(0, 5)} -{' '}
                      {item.endTime.substring(0, 5)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/80" data-id="element-3145">
                    <Icon className="w-4 h-4" data-id="element-3146" />
                    <span data-id="element-3147">Kurum Dersi</span>
                  </div>
                  {item.notes && <div className="mt-2 text-sm text-white/70" data-id="element-3148">
                      {item.notes}
                    </div>}
                </div>;
      }
      return <div key={item.id} className={`relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${gradientClass} ${item.completed ? 'opacity-60' : ''} group`} data-id="element-3149">
                <div className="bg-black/90 p-4 rounded-lg" data-id="element-3150">
                  <div className="absolute top-2 right-2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3151">
                    <button className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" title="Düzenle" onClick={() => onEditItem(item)} data-id="element-3152">
                      <Edit className="w-3.5 h-3.5" data-id="element-3153" />
                    </button>
                    <button className="p-1.5 rounded-full bg-white/10 hover:bg-red-500/20 text-white hover:text-red-400 transition-colors" title="Sil" onClick={() => onDeleteItem(selectedDay, item.id)} data-id="element-3154">
                      <Trash2 className="w-3.5 h-3.5" data-id="element-3155" />
                    </button>
                  </div>
                  <div className="flex items-center mb-4" data-id="element-3156">
                    <button onClick={() => onToggleCompletion(selectedDay, item.id)} className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors mr-3 ${item.completed ? 'bg-green-500 text-white' : 'bg-white/10'}`} data-id="element-3157">
                      {item.completed && <Check className="w-4 h-4" data-id="element-3158" />}
                    </button>
                    <div data-id="element-3159">
                      <h3 className={`text-lg font-semibold ${item.completed ? 'line-through text-gray-400' : ''}`} data-id="element-3160">
                        {item.subject}
                      </h3>
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-3161">
                    <div className="space-y-2" data-id="element-3162">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-id="element-3163">
                        <span className="text-gray-400 flex items-center gap-2" data-id="element-3164">
                          <Icon className="w-4 h-4 text-purple-400" data-id="element-3165" />
                          {item.activityType === 'study' ? 'Konu Çalışma' : item.activityType === 'practice' ? 'Soru Çözüm' : item.activityType === 'exam' ? 'Deneme' : item.activityType === 'video' ? 'Video İzleme' : item.activityType === 'resource' ? 'Kaynak Çalışması' : item.activityType === 'test' ? 'Test Çözümü' : item.activityType === 'homework' ? 'Ödev Yapma' : 'Etkinlik'}
                        </span>
                        <span className="text-sm text-gray-300" data-id="element-3166">
                          {item.startTime} - {item.endTime}
                        </span>
                      </div>
                      {item.questionCount > 0 && <div className="text-sm text-gray-400" data-id="element-3167">
                          Hedef: {item.questionCount} soru
                        </div>}
                      {item.notes && <div className="text-sm text-gray-400 mt-2" data-id="element-3168">
                          Not: {item.notes}
                        </div>}
                    </div>
                  </div>
                </div>
              </div>;
    }) : <div className="text-center py-8 text-gray-400" data-id="element-3169">
          Bu gün için henüz etkinlik eklenmemiş
        </div>}
    </div>;
}
// Weekly Summary Component showing all days with activity pills
function WeeklySummary({
  scheduleItems,
  onToggleCompletion,
  selectedDay
}: {
  scheduleItems: Record<string, ScheduleItem[]>;
  onToggleCompletion: (day: string, id: string) => void;
  selectedDay: string;
}) {
  const weekDays = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
  // Helper function to format subject names properly
  const formatSubjectName = (subject: string): string => {
    // Dictionary for Turkish subject names
    const subjectNameMap: Record<string, string> = {
      TÜRKÇE: 'Türkçe',
      TURKCE: 'Türkçe',
      MATEMATİK: 'Matematik',
      MATEMATİK: 'Matematik',
      'FEN BİLİMLERİ': 'Fen Bilimleri',
      'FEN BILIMLERI': 'Fen Bilimleri',
      'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK': 'T.C. İnkılap Tarihi',
      'T.C. INKILAP TARIHI VE ATATÜRKÇÜLÜK': 'T.C. İnkılap Tarihi',
      İNGİLİZCE: 'İngilizce',
      INGILIZCE: 'İngilizce',
      'DİN KÜLTÜRÜ VE AHLÂK BİLGİSİ': 'Din Kültürü',
      'DIN KÜLTÜRÜ VE AHLAK BILGISI': 'Din Kültürü'
    };
    // Return the properly formatted name if it exists in the map, otherwise capitalize first letter
    if (subjectNameMap[subject]) {
      return subjectNameMap[subject];
    }
    // If not in our dictionary, just capitalize first letter
    return subject.charAt(0).toUpperCase() + subject.slice(1).toLowerCase();
  };
  // Get gradient class for a subject
  const getSubjectGradient = (subject: string) => {
    const normalizedSubject = normalizeSubjectName(subject);
    return DERS_RENKLERI[normalizedSubject] || 'from-purple-600 to-indigo-600';
  };
  // Map subject to border color - Fixed to use correct color mapping
  const getSubjectBorderColor = (subject: string): string => {
    const normalizedSubject = normalizeSubjectName(subject);
    if (normalizedSubject === 'TÜRKÇE') return 'border-red-600';
    if (normalizedSubject === 'MATEMATİK') return 'border-blue-500';
    if (normalizedSubject === 'FEN BİLİMLERİ') return 'border-emerald-600';
    if (normalizedSubject === 'T.C. İNKILAP TARİHİ VE ATATÜRKÇÜLÜK') return 'border-amber-700';
    if (normalizedSubject === 'DİN KÜLTÜRÜ VE AHLÂK BİLGİSİ') return 'border-blue-900';
    if (normalizedSubject === 'İNGİLİZCE') return 'border-purple-500';
    // Default fallback
    return 'border-purple-600';
  };
  return <div className="space-y-6" data-id="element-3170">
      <h2 className="text-xl font-semibold" data-id="element-3171">Program Özeti</h2>
      <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600/50" data-id="element-3172">
        <div className="relative p-4 bg-black/90 rounded-lg" data-id="element-3173">
          <div className="grid grid-cols-7 gap-2" data-id="element-3174">
            {weekDays.map(day => {
            const dayItems = scheduleItems[day] || [];
            const isSelected = day === selectedDay;
            return <div key={day} className="space-y-3" data-id="element-3175">
                  <h3 className={`text-sm font-medium text-center py-2 px-2 rounded-lg ${isSelected ? 'bg-purple-900/50 text-white' : 'bg-purple-900/20 text-gray-300'}`} data-id="element-3176">
                    {day.substring(0, 3)}
                  </h3>
                  <div className="space-y-1.5" data-id="element-3177">
                    {dayItems.length > 0 ? dayItems.sort((a, b) => a.startTime.localeCompare(b.startTime)).map(item => {
                  const gradientClass = getSubjectGradient(item.subject);
                  const formattedSubject = formatSubjectName(item.subject);
                  const tooltipText = `${formattedSubject} - ${item.startTime.substring(0, 5)}${item.isInstitutional ? ' - Kurum Dersi' : ''}`;
                  // Institutional classes have a different style (filled colored blocks)
                  if (item.isInstitutional) {
                    return <div key={item.id} className={`px-2 py-1 rounded-full text-xs bg-gradient-to-r ${gradientClass} ${item.completed ? 'opacity-60' : ''}`} title={tooltipText} data-id="element-3178">
                                <div className="flex items-center justify-center" data-id="element-3179">
                                  <span className="truncate text-white" data-id="element-3180">
                                    {formattedSubject}
                                  </span>
                                </div>
                              </div>;
                  }
                  // Personal study sessions (floating cards with dark body and colored border)
                  // Fixed to use the correct border color based on the subject
                  const borderColorClass = getSubjectBorderColor(item.subject);
                  return <div key={item.id} className={`px-2 py-1 rounded-full text-xs border bg-black/80 ${borderColorClass} ${item.completed ? 'opacity-60' : ''}`} title={tooltipText} data-id="element-3181">
                              <div className="flex items-center justify-center" data-id="element-3182">
                                <span className="truncate" data-id="element-3183">
                                  {formattedSubject}
                                </span>
                              </div>
                            </div>;
                }) : <div className="text-center py-2 text-gray-500 text-xs" data-id="element-3184">
                        -
                      </div>}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}
// Weekly View Component showing a compact grid of days
function WeeklyView({
  scheduleItems,
  onSelectDay,
  onAddEvent,
  selectedDay
}: {
  scheduleItems: Record<string, ScheduleItem[]>;
  onSelectDay: (day: string) => void;
  onAddEvent: (day: string) => void;
  selectedDay: string;
}) {
  const weekDays = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
  const today = new Date().toLocaleString('tr-TR', {
    weekday: 'long'
  });
  const getActivityCounts = (day: string) => {
    const items = scheduleItems[day] || [];
    const personal = items.filter(item => !item.isInstitutional).length;
    const institutional = items.filter(item => item.isInstitutional).length;
    return {
      personal,
      institutional,
      total: items.length
    };
  };
  return <div className="grid grid-cols-7 gap-4" data-id="element-3185">
      {weekDays.map(day => {
      const isToday = day === today;
      const isSelected = day === selectedDay;
      const counts = getActivityCounts(day);
      return <div key={day} className={`relative cursor-pointer p-4 rounded-lg ${isSelected ? 'bg-gradient-to-br from-purple-900/40 to-indigo-900/40 border border-purple-500/30' : isToday ? 'bg-black/40 border border-purple-500/20' : 'bg-black/30 hover:bg-black/50'} transition-colors`} onClick={() => onSelectDay(day)} data-id="element-3186">
            <button onClick={e => {
          e.stopPropagation();
          onAddEvent(day);
        }} className="absolute top-2 right-2 p-1 bg-white/10 hover:bg-white/20 rounded-full text-purple-400 hover:text-white transition-colors" title="Etkinlik Ekle" data-id="element-3187">
              <Plus className="w-3.5 h-3.5" data-id="element-3188" />
            </button>
            <div className="text-center mb-3" data-id="element-3189">
              <div className={`font-medium ${isSelected ? 'text-purple-400' : isToday ? 'text-blue-400' : 'text-gray-300'}`} data-id="element-3190">
                {day.substring(0, 3)}
              </div>
            </div>
            <div className="flex flex-col items-center gap-1" data-id="element-3191">
              {counts.total > 0 ? <>
                  {counts.personal > 0 && <div className="flex justify-center gap-1" data-id="element-3192">
                      {Array.from({
                length: Math.min(counts.personal, 3)
              }).map((_, i) => <div key={`personal-${i}`} className="w-2.5 h-2.5 rounded-full bg-purple-500" title="Kişisel Çalışma" data-id="element-3193" />)}
                      {counts.personal > 3 && <div className="text-xs text-purple-400" data-id="element-3194">
                          +{counts.personal - 3}
                        </div>}
                    </div>}
                  {counts.institutional > 0 && <div className="flex justify-center gap-1" data-id="element-3195">
                      {Array.from({
                length: Math.min(counts.institutional, 3)
              }).map((_, i) => <div key={`inst-${i}`} className="w-2.5 h-2.5 rounded-full bg-blue-500" title="Kurum Dersi" data-id="element-3196" />)}
                      {counts.institutional > 3 && <div className="text-xs text-blue-400" data-id="element-3197">
                          +{counts.institutional - 3}
                        </div>}
                    </div>}
                </> : <div className="flex justify-center" data-id="element-3198">
                  <div className="w-2 h-2 rounded-full bg-gray-700" data-id="element-3199" />
                </div>}
            </div>
          </div>;
    })}
    </div>;
}
export function Schedule() {
  const [viewMode, setViewMode] = useState<'daily' | 'weekly'>('daily');
  const [selectedDay, setSelectedDay] = useState(new Date().toLocaleString('tr-TR', {
    weekday: 'long'
  }));
  const [scheduleItems, setScheduleItems] = useState<Record<string, ScheduleItem[]>>({});
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ScheduleItem | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    day: string;
    id: string;
  } | null>(null);
  const {
    gorevler
  } = useProgramContext();
  const institutionalClasses: Record<string, ScheduleItem[]> = {
    Pazartesi: [{
      id: 'inst-1',
      activityType: 'study',
      subject: 'MATEMATİK',
      startTime: '09:00',
      endTime: '10:30',
      notes: 'Okul dersi - Matematik',
      completed: false,
      isInstitutional: true
    }, {
      id: 'inst-2',
      activityType: 'study',
      subject: 'TÜRKÇE',
      startTime: '10:45',
      endTime: '12:15',
      notes: 'Okul dersi - Türkçe',
      completed: false,
      isInstitutional: true
    }],
    Çarşamba: [{
      id: 'inst-3',
      activityType: 'study',
      subject: 'FEN BİLİMLERİ',
      startTime: '13:00',
      endTime: '14:30',
      notes: 'Okul dersi - Fen Bilimleri',
      completed: false,
      isInstitutional: true
    }],
    Cuma: [{
      id: 'inst-4',
      activityType: 'exam',
      subject: 'İNGİLİZCE',
      startTime: '15:00',
      endTime: '16:30',
      notes: 'İngilizce konuşma kulübü',
      completed: false,
      isInstitutional: true
    }]
  };
  useEffect(() => {
    const savedSchedule = localStorage.getItem('schedule');
    let localScheduleItems: Record<string, ScheduleItem[]> = {};
    if (savedSchedule) {
      localScheduleItems = JSON.parse(savedSchedule);
    }
    const contextItems: Record<string, ScheduleItem[]> = {};
    gorevler.forEach(gorev => {
      const [startTime, endTime] = gorev.saatAraligi.split(' - ');
      const scheduleItem: ScheduleItem = {
        id: gorev.id,
        activityType: 'study',
        subject: gorev.dersAdi,
        startTime,
        endTime,
        notes: gorev.notlar,
        completed: gorev.tamamlandi
      };
      if (!contextItems[gorev.gun]) {
        contextItems[gorev.gun] = [];
      }
      contextItems[gorev.gun].push(scheduleItem);
    });
    const mergedItems: Record<string, ScheduleItem[]> = {};
    const mergeDay = (day: string) => {
      const dayItems = [...(localScheduleItems[day] || []), ...(contextItems[day] || []), ...(institutionalClasses[day] || [])];
      if (dayItems.length > 0) {
        mergedItems[day] = dayItems;
      }
    };
    const weekDays = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
    weekDays.forEach(mergeDay);
    setScheduleItems(mergedItems);
  }, [gorevler]);
  const handleAddEvent = (event: Omit<ScheduleItem, 'id' | 'completed'>) => {
    const newEvent: ScheduleItem = {
      ...event,
      id: Math.random().toString(36).substr(2, 9),
      completed: false
    };
    const updatedItems = {
      ...scheduleItems,
      [selectedDay]: [...(scheduleItems[selectedDay] || []), newEvent]
    };
    setScheduleItems(updatedItems);
    const storageItems: Record<string, ScheduleItem[]> = {};
    Object.keys(updatedItems).forEach(day => {
      const personalItems = updatedItems[day].filter(item => !item.isInstitutional);
      if (personalItems.length > 0) {
        storageItems[day] = personalItems;
      }
    });
    localStorage.setItem('schedule', JSON.stringify(storageItems));
  };
  const handleEditItem = (updatedItem: ScheduleItem) => {
    setEditingItem(updatedItem);
  };
  const handleSaveEdit = (day: string, updatedItem: ScheduleItem) => {
    const updatedItems = {
      ...scheduleItems,
      [day]: scheduleItems[day]?.map(item => item.id === updatedItem.id ? updatedItem : item)
    };
    setScheduleItems(updatedItems);
    const storageItems: Record<string, ScheduleItem[]> = {};
    Object.keys(updatedItems).forEach(day => {
      const personalItems = updatedItems[day].filter(item => !item.isInstitutional);
      if (personalItems.length > 0) {
        storageItems[day] = personalItems;
      }
    });
    localStorage.setItem('schedule', JSON.stringify(storageItems));
  };
  const confirmDeleteItem = (day: string, id: string) => {
    setItemToDelete({
      day,
      id
    });
    setShowConfirmDialog(true);
  };
  const handleDeleteItem = () => {
    if (!itemToDelete) return;
    const {
      day,
      id
    } = itemToDelete;
    const updatedItems = {
      ...scheduleItems,
      [day]: scheduleItems[day]?.filter(item => item.id !== id)
    };
    setScheduleItems(updatedItems);
    const storageItems: Record<string, ScheduleItem[]> = {};
    Object.keys(updatedItems).forEach(day => {
      const personalItems = updatedItems[day].filter(item => !item.isInstitutional);
      if (personalItems.length > 0) {
        storageItems[day] = personalItems;
      }
    });
    localStorage.setItem('schedule', JSON.stringify(storageItems));
    setShowConfirmDialog(false);
    setItemToDelete(null);
  };
  const toggleItemCompletion = (day: string, id: string) => {
    const updatedItems = {
      ...scheduleItems,
      [day]: scheduleItems[day]?.map(item => item.id === id ? {
        ...item,
        completed: !item.completed
      } : item)
    };
    setScheduleItems(updatedItems);
    const storageItems: Record<string, ScheduleItem[]> = {};
    Object.keys(updatedItems).forEach(day => {
      const personalItems = updatedItems[day].filter(item => !item.isInstitutional);
      if (personalItems.length > 0) {
        storageItems[day] = personalItems;
      }
    });
    localStorage.setItem('schedule', JSON.stringify(storageItems));
  };
  const handleDaySelect = (day: string) => {
    setSelectedDay(day);
  };
  const handleAddEventFromWeekly = (day: string) => {
    setSelectedDay(day);
    setShowAddEventModal(true);
  };
  const today = new Date().toLocaleString('tr-TR', {
    weekday: 'long'
  });
  return <div className="space-y-6" data-id="element-3200">
      <h1 className="text-2xl font-bold" data-id="element-3201">Programım</h1>
      <div className="flex justify-center mb-6" data-id="element-3202">
        <div className="inline-flex bg-black/30 rounded-lg p-1" data-id="element-3203">
          <button onClick={() => setViewMode('daily')} className={`px-4 py-2 rounded-lg transition-colors ${viewMode === 'daily' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' : 'text-gray-300 hover:bg-white/10'}`} data-id="element-3204">
            Günlük Görünüm
          </button>
          <button onClick={() => setViewMode('weekly')} className={`px-4 py-2 rounded-lg transition-colors ${viewMode === 'weekly' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' : 'text-gray-300 hover:bg-white/10'}`} data-id="element-3205">
            Haftalık Görünüm
          </button>
        </div>
      </div>

      {viewMode === 'daily' ? <div className="space-y-6" data-id="element-3206">
          <div className="flex justify-between items-center" data-id="element-3207">
            <h2 className="text-xl font-semibold flex items-center gap-2" data-id="element-3208">
              <CalendarIcon className="w-5 h-5 text-purple-400" data-id="element-3209" />
              {selectedDay === today ? 'Bugün' : selectedDay}
            </h2>
            <button onClick={() => setShowAddEventModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 transition-colors" data-id="element-3210">
              <Plus className="w-4 h-4" data-id="element-3211" />
              Etkinlik Ekle
            </button>
          </div>
          <DaySchedule scheduleItems={scheduleItems} selectedDay={selectedDay} onToggleCompletion={toggleItemCompletion} onEditItem={handleEditItem} onDeleteItem={confirmDeleteItem} data-id="element-3212" />
        </div> : <div className="space-y-6" data-id="element-3213">
          <h2 className="text-xl font-semibold mb-4" data-id="element-3214">Haftalık Program Özeti</h2>
          <div className="mb-2 flex justify-center gap-4 text-sm text-gray-400" data-id="element-3215">
            <div className="flex items-center gap-1" data-id="element-3216">
              <div className="w-2.5 h-2.5 rounded-full bg-purple-500" data-id="element-3217"></div>
              <span data-id="element-3218">Kişisel Çalışma</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-3219">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500" data-id="element-3220"></div>
              <span data-id="element-3221">Kurum Dersi</span>
            </div>
          </div>
          <WeeklyView scheduleItems={scheduleItems} onSelectDay={handleDaySelect} onAddEvent={handleAddEventFromWeekly} selectedDay={selectedDay} data-id="element-3222" />
          <WeeklySummary scheduleItems={scheduleItems} onToggleCompletion={toggleItemCompletion} selectedDay={selectedDay} data-id="element-3223" />
        </div>}

      {/* Modals */}
      {showAddEventModal && <AddEventModal isOpen={showAddEventModal} onClose={() => setShowAddEventModal(false)} onAddEvent={handleAddEvent} selectedDay={selectedDay} data-id="element-3224" />}

      {editingItem && <EditModal item={editingItem} day={selectedDay} onClose={() => setEditingItem(null)} onSave={handleSaveEdit} data-id="element-3225" />}

      <ConfirmDialog isOpen={showConfirmDialog} message="Bu etkinliği silmek istediğinize emin misiniz?" onConfirm={handleDeleteItem} onCancel={() => {
      setShowConfirmDialog(false);
      setItemToDelete(null);
    }} data-id="element-3226" />
    </div>;
}